// src/hooks/useModuleDeleteSettings.ts
/**
 * Reads a module's delete permissions from its own settings table.
 *
 * Both AC modules already carry a generic key/value settings table
 * (`{ settingID, name, value, description, isActive }`) served by a `CrudService`, so gating deletion
 * needs **no schema change** — only these rows, seeded per module:
 *
 * | Setting name | Meaning | Absent → |
 * | --- | --- | --- |
 * | `AllowDelete.<Scope>` | single-record delete on that page | falls back to `AllowDelete` |
 * | `AllowBulkDelete.<Scope>` | deleting a selection on that page | falls back to `AllowBulkDelete` |
 * | `AllowDelete` / `AllowBulkDelete` | module-wide default | **off** |
 * | `DeleteConfirmationText` | the word an admin must type | `DELETE` |
 *
 * `<Scope>` is the page: `Intakes`, `Registrations`, `Requests`, `Cases`. ⚠️ Scoped before module-wide,
 * because "clear spam out of the intake queue" and "delete a live registration" are very different
 * risks and an office should be able to permit one without the other.
 *
 * ⚠️ **Everything defaults to off.** A missing or unreadable setting must never be read as permission:
 * the failure mode of defaulting on is unrecoverable data loss, and the failure mode of defaulting off
 * is a disabled button. Settings are read per module deliberately — Grow and Counselling are run by
 * different offices and should be able to answer this question differently.
 */
import { useCallback, useEffect, useState } from 'react';

/** The shape both module settings services share (`CrudService.getAll`). */
interface SettingsSource {
  getAll: () => Promise<unknown>;
}

interface SettingRow {
  name?: string | null;
  value?: string | null;
  isActive?: boolean;
}

export interface ModuleDeleteSettings {
  allowDelete: boolean;
  allowBulkDelete: boolean;
  /** The word the admin types to confirm. Never empty. */
  confirmWord: string;
  loading: boolean;
  /** True when the settings could not be read at all — the UI should say so rather than imply "off". */
  failed: boolean;
  /**
   * Whether an `AllowDelete` row actually exists (scoped or module-wide).
   *
   * ⚠️ Only for pages whose delete **already worked** before this gate existed. There, defaulting to
   * off would remove a live capability the moment this shipped and before anyone had a row to turn on,
   * so those pages treat an absent row as "unchanged" and an explicit `false` as "off". A page whose
   * delete is new must use `allowDelete` on its own — for those, absent means off.
   */
  deleteConfigured: boolean;
}

const TRUTHY = new Set(['true', '1', 'yes', 'y', 'on', 'enabled']);
const isOn = (v?: string | null) => TRUTHY.has((v ?? '').trim().toLowerCase());

export function useModuleDeleteSettings(service: unknown, scope?: string): ModuleDeleteSettings {
  const [state, setState] = useState<Omit<ModuleDeleteSettings, 'loading' | 'failed'>>({
    allowDelete: false,
    allowBulkDelete: false,
    confirmWord: 'DELETE',
    deleteConfigured: false,
  });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    const svc = service as SettingsSource | undefined;
    if (typeof svc?.getAll !== 'function') { setLoading(false); return; }

    setLoading(true);
    setFailed(false);
    try {
      const res = await svc.getAll();
      // Read through the envelope, as elsewhere in this app.
      const raw = (res as { data?: unknown })?.data ?? res;
      const rows = (Array.isArray(raw) ? raw : []) as SettingRow[];

      const find = (name: string) => rows.find(
        (r) => (r.name ?? '').trim().toLowerCase() === name.toLowerCase() && r.isActive !== false,
      );

      /** Scoped setting wins; the module-wide one is the fallback. */
      const rowFor = (base: string) => (scope ? find(`${base}.${scope}`) : undefined) ?? find(base);
      const resolve = (base: string) => isOn(rowFor(base)?.value);

      const word = (find('DeleteConfirmationText')?.value ?? '').trim();
      setState({
        allowDelete: resolve('AllowDelete'),
        allowBulkDelete: resolve('AllowBulkDelete'),
        // An empty configured word would make the confirmation a no-op, so it falls back.
        confirmWord: word || 'DELETE',
        deleteConfigured: rowFor('AllowDelete') !== undefined,
      });
    } catch (e) {
      console.error('[useModuleDeleteSettings] could not read the module settings:', e);
      // ⚠️ Stays off. An unreadable setting is not permission.
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [service, scope]);

  useEffect(() => { load(); }, [load]);

  return { ...state, loading, failed };
}
